import type { NavigateFunction } from "react-router-dom";
import type { DataTableColumn } from "@/components/ui/data-table";
import { StatusBadge } from "@/components/ui/status-badge";
import type { Fund } from "@/lib/api/acquisitions";

export function createBudgetFundColumns(
  navigate: NavigateFunction,
  formatCurrency: (n: number) => string
): DataTableColumn<Fund>[] {
  return [
    {
      id: "name",
      header: "Fund",
      cell: (row) => (
        <button
          type="button"
          onClick={() => navigate(`/acquisitions/budget/funds/${row.id}`)}
          className="text-left font-medium text-primary hover:underline"
        >
          {row.name}
        </button>
      ),
    },
    {
      id: "code",
      header: "Code",
      cell: (row) => <span className="font-mono text-sm text-slate-600">{row.code}</span>,
      hideOnMobile: true,
      width: "w-20",
    },
    {
      id: "fy",
      header: "FY",
      cell: (row) => <span className="text-sm text-slate-600">{row.fiscalYear}</span>,
      width: "w-16",
      hideOnMobile: true,
    },
    {
      id: "allocation",
      header: "Assigned",
      cell: (row) => (
        <span className="text-sm font-medium tabular-nums">{formatCurrency(row.allocation)}</span>
      ),
      align: "right",
    },
    {
      id: "encumbered",
      header: "Encumbered",
      cell: (row) => (
        <span className="text-sm tabular-nums text-amber-600">
          {formatCurrency(row.encumbered)}
        </span>
      ),
      align: "right",
      hideOnMobile: true,
    },
    {
      id: "expended",
      header: "Expended",
      cell: (row) => (
        <span className="text-sm tabular-nums text-blue-600">{formatCurrency(row.expended)}</span>
      ),
      align: "right",
      hideOnMobile: true,
    },
    {
      id: "available",
      header: "Available",
      cell: (row) => (
        <span
          className={`text-sm font-semibold tabular-nums ${
            row.available < 0 ? "text-red-600" : "text-emerald-600"
          }`}
        >
          {formatCurrency(row.available)}
        </span>
      ),
      align: "right",
    },
    {
      id: "utilization",
      header: "Used",
      cell: (row) => (
        <div className="flex items-center gap-2">
          <div className="h-1.5 w-12 overflow-hidden rounded-full bg-slate-100">
            <div
              className={`h-full rounded-full ${
                row.utilizationPercent > 90
                  ? "bg-red-500"
                  : row.utilizationPercent > 70
                    ? "bg-amber-500"
                    : "bg-emerald-500"
              }`}
              style={{ width: `${Math.min(row.utilizationPercent, 100)}%` }}
            />
          </div>
          <span className="w-8 text-xs tabular-nums text-slate-500">{row.utilizationPercent}%</span>
        </div>
      ),
      width: "w-28",
    },
    {
      id: "status",
      header: "Status",
      cell: (row) => (
        <StatusBadge variant={row.isActive ? "success" : "neutral"}>
          {row.isActive ? "Active" : "Inactive"}
        </StatusBadge>
      ),
      width: "w-24",
      hideOnMobile: true,
    },
  ];
}
